import React, { PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { useDevCycleClient } from "@devcycle/react-client-sdk";
import * as Sentry from "@sentry/react";
import { v4 as uuidv4 } from "uuid";

import { CurrentUserData } from "./hooks/api/users/useCurrentUser";
import { getCompanyPaymentType, getTrialDate, getTrialStatus } from "./hooks/subscription";
import { DataTypeName } from "./types/models";
import { camelToSnakeCase, isDefined } from "./utils";

/**
 * We have to handle the case when we haven't loaded amplitude at all (in development)
 * but also we should call window.amplitude.getInstace() only after its javascript is loaded
 * @returns the amplitude client
 */
function getAmplitudeInstance() {
  return window.amplitude;
}

// INFO: new events
export enum EventNames {
  navToSourceDocs = "Navigated to source docs",
  searchPerformed = "Search Performed",
  exportedData = "Exported Data",
  descriptionBoxOpened = "Description Preview Box Opened",
  shareEmailModalOpen = "Share via email modal opened",
  shareEmailSent = "Records shared via email",
  noticeDetailsCopied = "Notice details copied to clipboard",
  resizeTableColumn = "Resized table column",
  clickedLetsChat = `Clicked "Let's Chat" button`,
  paywallViewed = `Viewed paywall`,
  clickedRequestPro = "See Stotles Pro in action",
  userAccountCreated = "Onboarding: New user account created",
  recordQueryCreated = "Onboarding: New record query created",
  recordQueryUpdated = "Existing record query updated",
  customKeywordAdded = "Onboarding: Custom keyword added",
  clickedSignUpCTA = 'Clicked "Sign Up" CTA',
  clickedAlreadyVerified = 'Onboarding: Clicked "Already Verified?" button',
  clickedNoEmailReceived = 'Onboarding: Clicked "Didn\'t get an email?" button',
  clickedReSendEmail = 'Onboarding: Clicked "Re-send" email button',
  clickedHelpCentre = 'Clicked "Help centre" link',
  clickedPlaybook = "Playbook link clicked",
  feedPreviewFilterButtonClicked = "Feed preview filter button clicked",
  feedPreviewFiltered = "Feed preview filter applied",
  viewSelected = "View selected",
  viewCreated = "View created",
  viewUpdated = "View updated",
  viewDeleted = "View deleted",
  columnEdit = "Columns edited",
  allViewsTableOpened = "All views table opened",
  userInviteResent = "User invite re-sent",
  accountBriefingBtnClicked = "Get account briefing button clicked",
  listDeleted = "List deleted",
  listCreated = "List created",
  listUpdated = "List updated",
  listDuplicated = "List duplicated",
  recordAddedToList = "Record added to list",
  recordRemovedFromList = "Record removed from list",
  clickedWhatsNew = "Page View: What's new",
  summaryViewChanged = "Summary view changed",
  summaryTimelineChanged = "Summary timeline changed",
  buyerUrlClicked = "Buyer URL clicked",
  seeMatchingExpiriesClicked = " See expiries matching my settings clicked",
  tabChanged = "Tab changed",
  emailCopied = "Email copied to clipboard",
  phoneNumberCopied = "Phone copied to clipboard",
  paginationSelected = "Pagination selected",
  linkedinClicked = "LinkedIn clicked",
  reportRecordListItemClicked = "Record list item clicked",
  reportLinkedListItemClicked = "Link list item clicked",
  cpvChartClicked = "CPV chart clicked",
  reportSupplierTableProfileClicked = "Supplier table clicked profile",
  reportSupplierTableAwardsClicked = "Supplier table clicked awards",
  recordPreviewSlideoutOpened = "Record preview slide-out opened",
  qualificationStarted = "Qualification started",
  qualificationActioned = "Qualification actioned",
  notificationCentreOpened = "Notification centre opened",
  notificationClick = "Notification clicked",
  notificationMarkedAsRead = "Notification marked as read",
  notificationMarkedAsUnread = "Notification marked as unread",
  commentActioned = "Comment actioned",
  assigneeActioned = "Assignee actioned",
  documentSourceViewed = "Document source viewed",
  buyerSaved = "Buyer saved",
  buyerBulkSave = "Buyers bulk save",
  buyerBulkRemove = "Buyers bulk removed",
  buyerRemoved = "Buyer removed",
  buyerListCreated = "Buyer list created",
  buyerListDeleted = "Buyer list deleted",
  buyerListRenamed = "Buyer list renamed",
  supplierSaved = "Supplier saved",
  supplierBulkSaved = "Supplier bulk saved",
  highlightClicked = "Highlight clicked",
  filterActioned = "Filter actioned",
  filterCollapseActioned = "Filter collapse actioned",
  recordDrilldownOpened = "Record drill-down opened",
  savedViewPinned = "Saved view pinned",
  pinnedSavedViewRankChange = "Pinned saved view ranking changed",
  savedViewUnpinned = "Saved view unpinned",
  sendToCRMModalOpened = "Send to CRM modal opened",
  sendToCRMModalCancelled = "Send to CRM modal cancelled",
  sendToCRMClicked = "Send to CRM clicked",
  objectSentToCRM = "Object sent to CRM",
  viewInCRMButtonClicked = "View in CRM button clicked",
  connectToCRMButtonClicked = "Go to integrations modal opened",
  closeCRMModelButtonClicked = "Go to Integrations page closed",
  goToIntegrationsPageClicked = "Go to Integrations Page button clicked",
  connectToIntegrationClicked = "Connect to integration clicked",
  disconnectFromIntegrationClicked = "Disconnect from integration clicked",
  integrationSuccessfullyConnected = "Integration successfully connected",
  viewFeedFromIntegrationClicked = "Go to feed clicked",
  backButtonFromIntegrationClicked = "Back to integrations page clicked",
  setUpIntegrationGuidelinesClicked = "Set up integration guidelines clicked ",
  learnMoreIntegrationModalOpened = "Learn more about integration modal opened",
  fieldAddedForIntegration = "Integration form field added",
  fieldRemovedForIntegration = "Integration form field removed",
  fieldMarkedRequiredForIntegration = "Integration configuration - required field actioned",
  fieldMarkedNotRequiredForIntegration = "Mark as not required",
  configureIntegrationClicked = "Configure integration clicked",
  settingsSavedForIntegration = "Integration settings saved",
  noticeListClicked = "Notice list clicked",
  noticeListTabChanged = "Notice list tab changed",
  buyerListClicked = "Buyer list clicked",
  groupBySelected = "Grouped by selected",
  chartClickedOn = "Chart Clicked On",
  dateUnitsSelected = "DateUnitsSelected",
  chartTypeSelected = "Chart type selected",
  openOutreachModal = "Outreach modal opened",
  generateOutreachClicked = "Generate outreach clicked",
  refreshOutreachClicked = "Refresh outreach clicked",
  skipGenerateOutreachClicked = "Skip outreach generation clicked",
  outreachCopy = "Outreach copied to clipboard",
  sendOutreach = "Send via email clicked",
  productTourActioned = "Onboarding tooltip actioned",
  productTourHidden = "Onboarding tooltip hidden",
  productTourCompleted = "Onboarding tooltip marked as complete",
  userLike = "User likes",
  userDislike = "User dislikes",
  dismissFeedback = "Dimiss feedback",
  submittedFeedback = "Submitted feedback",
  clickedExternalLink = "Clicked external link",
  paywallModalOpened = "Paywall modal opened",
  paywallModalDismissed = "Paywall modal dismissed",
  paywallActioned = "Paywall actioned",
  industryReportViewed = "Industry report viewed",
  industryReportLinkCopied = "Industry report link copied to clipboard",
  customBriefingViewed = "Custom briefing viewed",
  customBriefingLinkCopied = "Custom briefing link copied to clipboard",
  customBriefingDownloaded = "Custom briefing downloaded",
  tableSorted = "Table sorted",
  freeSearch = "Free search",
  filterPanelOpened = "Filter panel opened",
  relevanceScoreClicked = "Signal score popover opened",
  relevanceScoreUpdateSettings = "Signal settings opened",
  alertModalOpened = "Set view alert modal opened",
  alertModified = "Alert modified",
  promptActioned = "Prompt actioned",
  feedbackModalOpened = "Feedback modal opened",
  negativeFeedbackSubmitted = "Negative feedback submitted",
  negativeFeedbackDismissed = "Negative feedback dismissed",
  positiveFeedbackSubmitted = "Positive feedback submitted",
  positiveFeedbackDismissed = "Positive feedback dismissed",
  newChatCreated = "Ask AI: New chat created",
  chatSelected = "Ask AI: Chat selected",
  visitStotlesClicked = "Visit stotles clicked",
  shareReportClicked = "Share report clicked",
  signUpForFreeClicked = "Sign up for free clicked",
  bookADemoClicked = "Book a demo clicked",
  bookADemoRequested = "Book a demo requested",
  confirmationMessageClosed = "Confirmation message closed",
  bookADemoCancelled = "Book a demo cancelled",
  aiReportViewed = "Page View: Report",
  shareReportModalOpened = "Share as report modal opened",
  aiReportGenerated = "Report generated",
  frameworkClicked = "Framework clicked",
  suppliersClicked = "Suppliers clicked",
  callOffClicked = "Call-offs clicked",
  breadcrumbClicked = "Breadcrumb clicked",
  noticeClicked = "Notice clicked",
  navToBuyerProfile = "Navigated to Buyer Profile",
  answerGenerated = "Answer generated",
  newTeamCreated = "New team created",
  joinTeamClicked = "Join team clicked",
  createNewTeamClicked = "Create new team clicked",
  contactSupportClicked = "Contact support clicked",
  joinExistingTeamClicked = "Join existing team clicked",
  joinTeamModalClosed = "Join team modal closed",
  saveSignalsModalActioned = "Save signals confirm modal actioned",
  signalsEdited = "Signals edited (Before Saving)",
  suggestedSignalRefresh = "Suggested signal refreshed",
  emailLinkClicked = "E-mail link clicked",
  documentPreviewOpened = "Document preview slideout opened",
  documentSearched = "Document searched",
  scheduleReminderModalOpened = "Schedule reminder modal opened",
  reminderSavedToCalendar = "Reminder saved to calendar",
  bidDetailsUpdated = "Bid details updated",
  planUpgradeClicked = "Plan upgrade clicked",
  planDowngradeClicked = "Plan downgrade clicked",
  creditLimitReached = "Credit limit reached",
  pipelineViewed = "Pipeline viewed",
  videoViewed = "Video viewed",
  qualificationReportActioned = "Qualification report actioned",
  qualificationReportCreated = "Qualification report created",
  qualificationReportDeleted = "Qualification report deleted",
  qualificationReportRegenerated = "Qualification report regenerated",
  tenderDocumentsTrayOpened = "Tender documents tray opened",
  tenderDocumentOpened = "Tender document opened",
  citationOpened = "Citation opened",
  saveDataClicked = "Save data clicked",
  qualReportLinkCopied = "Qualification report share link copied",
  qualificationCriteriaEdited = "Qualification criteria edited",
  stotlesAiOpened = "Stotles AI opened",
  additionalDocumentsUploaded = "Additional tender documents uploaded",
  additionalDocumentsRegenerated = "Additional documents regenerated",
  createStrategyStarted = "Create strategy started",
  buildPipelineStarted = "Build pipeline started",
  trackTendersStarted = "Track tenders started",
  findBuyerDocumentsStarted = "Find buyer documents started",
  winBidsStarted = "Win bids started",
  bidReportOptionSelected = "Bid report option selected",
  marketAnalysisCreated = "Market analysis created",
  marketAnalysisFilterChanged = "Market analysis filter changed",
  marketAnalysisExported = "Market analysis exported",
  bidSelfServeSaved = "Bid / no-bid criteria settings saved",
  newQuestionAsked = "Ask AI: New question asked",
  quickPromptClicked = "Ask AI: Quick prompt clicked",
  quickPromptsEditClicked = "Ask AI: Edit quick prompts clicked",
  referenceLinkOpened = "Ask AI: Reference link opened",
  bidDocumentDeleted = "Bid document deleted",
  bidDocumentProcessedSuccessfully = "Bid document processed successfully",
  bidDocumentProcessingError = "Bid document processing error",
  customerDocumentProcessedSuccessfully = "Customer document processed successfully",
  customerDocumentProcessingError = "Customer document processing error",
  copyToClipboardClicked = "Ask AI: Copy to clipboard clicked",
  toneOfVoiceGuideCreated = "Tone of voice guide created",
  toneOfVoiceGuideEdited = "Tone of voice guide edited",
  toneOfVoiceGuideDeleted = "Tone of voice guide deleted",
  quickPromptCreated = "Quick prompt created",
  quickPromptEdited = "Quick prompt edited",
  quickPromptDeleted = "Quick prompt deleted",
  quickPromptsReordered = "Quick prompts reordered",
  quickPromptsReset = "Quick prompts reset",
  tovSelectionChanged = "Ask AI: Tone of voice changed",
  bidResponseCreated = "Bid response created",
  bidResponseUpdated = "Bid response updated",
  bidResponseQuestionExtractionStarted = "Bid response question extraction started",
  bidDataAdded = "Bid data added",
  bidDataRemoved = "Bid data removed",
  createLeadStarted = "Create lead started",
  leadDetailsUpdated = "Lead details updated",
  leadProgressed = "Lead progressed",
  leadLost = "Lead lost",
  leadDataAdded = "Lead data added",
  leadDataRemoved = "Lead data removed",
  leadCreated = "Lead created",
  bidCreated = "Bid created",
  bidProgressed = "Bid progressed",
  bidLost = "Bid lost",
  topicSearchPerformed = "Topic search performed",
  topicSearchResultsReturned = "Topic search results returned",
  contactsRevealed = "Contacts revealed",
  contactsExportStarted = "Contacts export started",
  recommendedContactDismissed = "Recommended contact dismissed",
  moreRecommendedContactsRequested = "More recommended contacts requested",
  noticeInsightsRailViewed = "Notice insights rail viewed",
  insightSupplierCardOpened = "Insight supplier card opened",
  noticeRelationshipOpened = "Notice relationship opened",
}

// Source location of where the event is triggered
// **Keep in sync with getPageSource below**

enum EventPageSources {
  myFeed = "My Feed",
  userLists = "User Lists",
  buyerSearch = "Buyer Search",
  buyerSupplierRelationship = "Buyer Supplier Relationship",
  savedBuyersPage = "Buyer lists",
  relevantBuyersPage = "Relevant Buyers",
  supplierSearch = "Supplier Search",
  recordSearch = "Record Search",
  buyerProfile = "Buyer Profile",
  buyerLists = "Buyer Lists",
  buyerList = "Buyer List",
  supplierProfile = "Supplier Profile",
  recordDetailsPage = "Record Details Page",
  feedSettingsPage = "Feed Settings Page",
  onboardingPage = "Onboarding Page",
  signUpPage = "Sign Up Page",
  accountPage = "Account Management Page",
  accountPageSubscription = "Account Management Page - Subscription",
  reportPage = "Report Page",
  partnerListPage = "My Partners List Page",
  competitorListPage = "My Competitors List Page",
  partnerProfile = "Partner Profile",
  competitorProfile = "Competitor Profile",
  viewDocumentPage = "View document page",
  reportsPage = "Reports",
  unknown = "Unknown Page",
  savedNoticesPage = "Saved Notices Page",
  outreachOpportunitiesList = "Outreach opportunities list",
  spendData = "Spend data",
  documentData = "Document data",
  contacts = "Contact search",
  relatedContacts = "Related contacts",
  outreachModal = "Outreach modal",
  getStarted = "Get started",
  buyerCleaning = "Buyer cleaning",
  frameworkSearchPage = "Framework search",
  frameworkDetailsPage = "Framework details",
  homepage = "Homepage",
  bidNoBidReport = "Bid / no-bid report",
  bidNoBidReportPublic = "Public Bid / no-bid report",
  qualificationReport = "Qualification report",
  qualificationReportPublic = "Public Qualification report",
  bidsReportsPage = "Bid reports Page",
  noticesAnalysis = "Notices Analysis Page",
  noticesV2Page = "Find notices 2.0",
  bidSelfServe = "Bid / no-bid criteria settings",
  quickPromptsSettings = "Quick prompts settings",
  bidResponsesPage = "Bid responses page",
  bidResponseDraftPage = "Bid response draft page",
  contentLibrary = "Content library",
  bidBuyerActivityPage = "Bid Buyer Activity Page",
  savedSearchPage = "Saved searches page",
  bidsPage = "Bids page",
  bidsDetailPage = "Bids detail page",
  bidWinThemesPage = "Bid win themes page",
  leadsPage = "Leads page",
  leadsDetailPage = "Leads detail page",
}

export enum AlertModificationType {
  frequency = "Frequency",
  created = "Created",
  deleted = "Deleted",
}

const OPT_OUT_DOMAINS = ["@stotles.com", "@hitechdigital.com", "@test.stotles.com"];

export function getPageSource(): EventPageSources {
  const urlPath = window.location.pathname;
  switch (urlPath) {
    case String(urlPath.match(/^\//)):
      return EventPageSources.homepage;
    case String(urlPath.match(/^\/account-management\/feed-settings.*/)):
      return EventPageSources.feedSettingsPage;
    case String(urlPath.match(/^\/account-management\/subscription.*/)):
      return EventPageSources.accountPageSubscription;
    case String(urlPath.match(/^\/account-management\/bid-self-serve.*/)):
      return EventPageSources.bidSelfServe;
    case String(urlPath.match(/^\/account-management\/quick-prompts.*/)):
      return EventPageSources.quickPromptsSettings;
    case String(urlPath.match(/^\/account-management.*/)):
      return EventPageSources.accountPage;
    case String(urlPath.match(/^\/notices\/analysis.*/)):
      return EventPageSources.noticesAnalysis;
    case String(urlPath.match(/^\/noticesv2/)):
      return EventPageSources.noticesV2Page;
    case String(urlPath.match(/^\/notices/)):
      return EventPageSources.myFeed;
    case String(urlPath.match(/^\/lists.*/)):
      return EventPageSources.userLists;
    case String(urlPath.match(/^\/buyers\/search.*/)):
      return EventPageSources.buyerSearch;
    case String(urlPath.match(/^\/suppliers\/search.*/)):
      return EventPageSources.supplierSearch;
    case String(urlPath.match(/^\/search\/saved.*/)):
      return EventPageSources.savedSearchPage;
    case String(urlPath.match(/^\/search.*/)):
      return EventPageSources.recordSearch;
    case String(urlPath.match(/^\/buyers\/saved\/key-accounts/)):
      return EventPageSources.savedBuyersPage;
    case String(urlPath.match(/^\/buyers\/lists/)):
      return EventPageSources.buyerLists;
    case String(urlPath.match(/^\/buyers\/lists\/.*/)):
      return EventPageSources.buyerList;
    case String(urlPath.match(/^\/buyers\/relevant-buyers.*/)):
      return EventPageSources.relevantBuyersPage;
    case String(urlPath.match(/^\/buyers\/all-buyers.*/)):
      return EventPageSources.buyerSearch; // This is TBD, it might be renamed to "All buyers"
    case String(urlPath.match(/^\/buyers\/.*/)):
      return EventPageSources.buyerProfile;
    case String(urlPath.match(/^\/suppliers\/.*/)):
      return EventPageSources.supplierProfile;
    case String(
      urlPath.match(/^\/bids\/[0-9a-f-]+\/requirement-summary\/[0-9a-f-]+\/bid-self-serve/),
    ):
    case String(
      urlPath.match(/^\/bids\/[0-9a-f-]+\/bid_qualification\/[0-9a-f-]+\/bid-self-serve/),
    ):
      return EventPageSources.bidSelfServe;
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+\/requirement-summary\/.*/)):
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+\/bid_qualification\/.*/)):
      return EventPageSources.bidNoBidReport;
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+\/qualification-report\/.*/)):
      return EventPageSources.qualificationReport;
    case String(urlPath.match(/^\/records\/.*/)):
      return EventPageSources.recordDetailsPage;
    case String(urlPath.match(/^\/onboarding$/)):
      return EventPageSources.onboardingPage;
    case String(urlPath.match(/^\/report\/.*/)):
      return EventPageSources.reportPage;
    case String(urlPath.match(/^\/partners/)):
      return EventPageSources.partnerListPage;
    case String(urlPath.match(/^\/competitors/)):
      return EventPageSources.competitorListPage;
    case String(urlPath.match(/^\/partners\/.*/)):
      return EventPageSources.partnerProfile;
    case String(urlPath.match(/^\/competitors\/.*/)):
      return EventPageSources.competitorProfile;
    case String(urlPath.match(/^\/reports\/.*/)):
      return EventPageSources.reportsPage;
    case String(urlPath.match(/^\/notices\/lists/)):
      return EventPageSources.savedNoticesPage;
    case String(urlPath.match(/^\/notices\/.*/)):
      return EventPageSources.recordDetailsPage;
    // This might need to be adjusted if we add routing for the wizard steps
    case String(urlPath.match(/^\/outreach/)):
      return EventPageSources.outreachOpportunitiesList;
    case String(urlPath.match(/^\/contacts.*/)):
      return EventPageSources.contacts;
    case String(urlPath.match(/^\/spend.*/)):
      return EventPageSources.spendData;
    case String(urlPath.match(/^\/documents.*/)):
      return EventPageSources.documentData;
    case String(urlPath.match(/^\/auth\/register.*/)):
      return EventPageSources.getStarted;
    case String(urlPath.match(/^\/tools\/buyer_cleaning.*/)):
      return EventPageSources.buyerCleaning;
    case String(urlPath.match(/^\/frameworks/)):
      return EventPageSources.frameworkSearchPage;
    case String(urlPath.match(/^\/frameworks\/.*/)):
      return EventPageSources.frameworkDetailsPage;
    case String(urlPath.match(/^\/leads\/[0-9a-f-]+/)):
      return EventPageSources.leadsDetailPage;
    case String(urlPath.match(/^\/leads/)):
      return EventPageSources.leadsPage;
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+\/win-themes/)):
      return EventPageSources.bidWinThemesPage;
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+\/buyer-activity/)):
      return EventPageSources.bidBuyerActivityPage;
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+\/bid_responses\/[0-9a-f-]+/)):
      return EventPageSources.bidResponseDraftPage; // TODO: verify if correct
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+\/bid_responses\/?.*$/)):
      return EventPageSources.bidResponsesPage; // TODO: verify if correct
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+\/tender-documents/)):
    case String(urlPath.match(/^\/bids\/[0-9a-f-]+/)):
      return EventPageSources.bidsDetailPage;
    case String(urlPath.match(/^\/bids/)):
      return EventPageSources.bidsPage;
    case String(urlPath.match(/^\/shared\/bid_reports\/[^/]+/)):
      return EventPageSources.bidNoBidReportPublic;
    case String(urlPath.match(/^\/shared\/qualification_report\/[^/]+/)):
      return EventPageSources.qualificationReportPublic;
    case String(urlPath.match(/^\/bid_reports\/[0-9a-f-]+\/bid-qualification/)):
      return EventPageSources.bidNoBidReport;
    case String(urlPath.match(/^\/bid_reports\/[0-9a-f-]+\/bid-self-serve/)):
      return EventPageSources.bidSelfServe;
    case String(urlPath.match(/^\/bid_reports\/[0-9a-f-]+\/bid_responses\/[0-9a-f-]+/)):
      return EventPageSources.bidResponseDraftPage;
    case String(urlPath.match(/^\/bid_reports/)):
      return EventPageSources.bidsReportsPage;
    case String(urlPath.match(/^\/bid_responses\/[0-9a-f-]+/)):
      return EventPageSources.bidResponseDraftPage;
    case String(urlPath.match(/^\/bid_responses\/?.*$/)):
      return EventPageSources.bidResponsesPage;
    case String(urlPath.match(/^\/content-library/)):
    case String(urlPath.match(/^\/content-library\/.*/)):
      return EventPageSources.contentLibrary;
    case String(urlPath.match(/^\/admin\/.*/)):
      return EventPageSources.unknown;
    default:
      if (process.env.NODE_ENV === "development") {
        throw new Error(`No page source matched for ${urlPath} - please specify one`);
      } else {
        Sentry.captureException(`Unknown page in tracking code: ${urlPath}`);
      }
      return EventPageSources.unknown;
  }
}

// Data type the event concerns
export enum EventDataTypes {
  record = "Record(s)",
  buyer = "Buyer(s)",
  supplier = "Supplier(s)",
  contact = "Contact(s)",
  notices = "Notice(s)",
  recordList = "Record List(s)",
  frameworks = "Framework(s)",
  callOff = "Call-off(s)",
  documents = "Document(s)",
  lead = "Lead(s)",
  bids = "Bid(s)",
  bidResponse = "Bid response(s)",
  noticeSemantics = "Notice Semantic(s)",
  spend = "Spend",
  signalSettings = "Signal settings",
}

// Attributes logged in amplitude
enum Attributes {
  isGuestUser = "Is guest user",
  companyName = "company_name",
  companyId = "company_id",
  companyGuid = "company_guid",
  companyIsDemo = "company_is_demo",
  enabledDataTypes = "enabled_data_types",
  selfServe = "self_serve",
  trialStatus = "trial_status",
  trialEnd = "trial_end",
  referrerUrl = "stotles_referrerUrl",
  firstPageVisit = "first_page_visit",
  userEmail = "email",
  userGuid = "user_guid",
  teamId = "stotles_team_id",
  teamName = "stotles_team_name",
}

// User properties only known once React state resolves (e.g. a DevCycle flag). Kept so the
// consent-gated Amplitude load can still apply them when it identifies the user.
const lateUserProperties: Record<string, string | number | boolean> = {};

/**
 * Function initializes amplitude tracking of users
 * @returns
 */
export function init(currentUser: CurrentUserData | undefined): void {
  try {
    if (!currentUser) {
      console.log("Cannot initialize tracking as there is no user present");
      return;
    } else {
      // Calling these methods here so the methods only have to be called once
      // Was more relevant when we had multiple tracking solutions in place
      const dataTypeSubs = currentUser.dataTypeSubscriptions
        .filter((s) => !s.disabled)
        .map((s) => s.dataTypeName as DataTypeName);

      identifyAmplitudeAttributes(currentUser, dataTypeSubs);

      // Amplitude initialisation is consent-gated and may not have loaded yet.
      // Register a callback so identification runs once Amplitude becomes available.
      window.stotlesOnAmplitudeReady = () => {
        identifyAmplitudeAttributes(currentUser, dataTypeSubs);
      };
    }
  } catch (e) {
    // Make sure that tracking exceptions don't break the website
    Sentry.captureException(e);
  }
}

/**
 * Initializes amplitude tracking. Identifies the attributes of a user and stores these
 * values in the amplitude instance
 * @param amplitudeInstance the amplitude client object
 * @returns
 */
function identifyAmplitudeAttributes(currentUser: CurrentUserData, dataTypeSubs: DataTypeName[]) {
  const amplitudeInstance = getAmplitudeInstance();
  if (!amplitudeInstance) {
    console.debug("No amplitude instance - amplitude tracking disabled?");
    return;
  }
  amplitudeInstance.setUserId(currentUser.email);
  const identify = new window.amplitude.Identify();
  identify.set(Attributes.userGuid, currentUser.guid);

  const currentUserCompany = currentUser.company;
  if (currentUserCompany) {
    identify.set(Attributes.companyName, currentUserCompany.name);
    identify.set(Attributes.companyId, currentUserCompany.id);
    identify.set(Attributes.companyGuid, currentUserCompany.guid);
    identify.set(Attributes.companyIsDemo, currentUserCompany.demo ? true : false);
  }
  identify.set(Attributes.enabledDataTypes, dataTypeSubs);
  identify.set(
    Attributes.trialStatus,
    getTrialStatus(getTrialDate(currentUser, "trialStart"), getCompanyPaymentType(currentUser)),
  );
  if (currentUser.team) {
    identify.set(Attributes.teamId, currentUser.team.id);
    identify.set(Attributes.teamName, currentUser.team.name);
  }
  if (currentUserCompany?.trialEnd) {
    identify.set(Attributes.trialEnd, currentUserCompany.trialEnd);
  }
  if (currentUser.signupUtm?.referrer) {
    identify.set(Attributes.referrerUrl, currentUser.signupUtm.referrer);
  }
  if (currentUser.signupUtm?.url) {
    identify.set(Attributes.firstPageVisit, currentUser.signupUtm.url);
  }

  if (currentUser.signupUtm?.utmParams) {
    // utmParams is a JSON-encoded string on the GraphQL payload; parse it (ignoring
    // invalid JSON) before iterating, otherwise Object.entries walks the string's characters.
    let parsedUtmParams: Record<string, string> | null = null;
    try {
      const parsed: unknown = JSON.parse(currentUser.signupUtm.utmParams);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        parsedUtmParams = parsed as Record<string, string>;
      }
    } catch {
      // Ignore invalid JSON
    }
    if (parsedUtmParams) {
      Object.entries(parsedUtmParams).forEach(([k, v]) => {
        if (!v) {
          return;
        }
        identify.set(`stotles_utm_${camelToSnakeCase(k)}`, v);
      });
    }
  }

  for (const [property, value] of Object.entries(lateUserProperties)) {
    identify.set(property, value);
  }

  identify.unset(Attributes.isGuestUser);
  if (currentUserCompany?.analyticsTags) {
    // analyticsTags is a structured object on the currentUser payload; set each non-null tag
    // (skipping the GraphQL __typename field).
    // TODO: We could be calling amplitude API when this data is changed in our DB
    for (const [tag, value] of Object.entries(currentUserCompany.analyticsTags)) {
      if (tag === "__typename") {
        continue;
      }
      // Only skip null/undefined — valid falsy values (e.g. companyIsIcp: false) must be sent.
      if (isDefined(value)) {
        // analyticsTags keys arrive camelCase from GraphQL; Amplitude expects snake_case.
        identify.set(camelToSnakeCase(tag), value);
      }
    }
  }
  amplitudeInstance.identify(identify);

  const optOutDomain = OPT_OUT_DOMAINS.find((domain) => currentUser.email.endsWith(domain));
  if (optOutDomain) {
    console.log(`Opting out of amplitude for ${optOutDomain} user`);
    amplitudeInstance.setOptOut(true);
  }
}

/**
 * Sets user properties that aren't on the current user payload. Applied straight away when
 * Amplitude is loaded, and again whenever it identifies the user later.
 */
export function setUserProperties(properties: Record<string, string | number | boolean>): void {
  Object.assign(lateUserProperties, properties);
  const amplitudeInstance = getAmplitudeInstance();
  if (!amplitudeInstance) return;
  const identify = new amplitudeInstance.Identify();
  for (const [property, value] of Object.entries(properties)) {
    identify.set(property, value);
  }
  amplitudeInstance.identify(identify);
}

export function logout(): void {
  // So the next user to sign in on this page isn't identified with this one's values.
  for (const property of Object.keys(lateUserProperties)) {
    delete lateUserProperties[property];
  }
  const amplitudeInstance = getAmplitudeInstance();

  if (!amplitudeInstance) {
    return;
  }
  amplitudeInstance.setUserId(undefined);
  amplitudeInstance.setDeviceId(uuidv4());
}

export type EventData = {
  "Page source"?: string;
  "Context source"?: string;
  [key: string]: unknown | undefined;
};

/**
 * @deprecated Use const { logEvent } = useTracking() instead
 * @param eventName
 * @param data
 */
export function logEvent(eventName: EventNames, data: EventData): void {
  if (!data["Page source"]) {
    data["Page source"] = getPageSource();
  }

  if (process.env.NODE_ENV === "development") {
    console.log(`Event: '${eventName}' with data '${JSON.stringify(data)}'`);
  }

  // Tracking the event in amplitude
  const amplitudeInstance = getAmplitudeInstance();
  if (amplitudeInstance) {
    amplitudeInstance.track(eventName, data);
  }
}

function sendGtagEvent(eventName: string, eventParameters: Record<string, unknown>): Promise<void> {
  const { gtag } = window;
  if (gtag) {
    return new Promise((accept) =>
      // event_callback: see https://developers.google.com/tag-platform/gtagjs/reference/parameters
      gtag("event", eventName, { ...eventParameters, event_callback: accept }),
    );
  } else {
    return Promise.resolve();
  }
}

export async function logUserConversion(): Promise<void> {
  // Conversion for linkedin campaigns
  if (window.lintrk) {
    window.lintrk("track", { conversion_id: 5410305 });
  }

  await sendGtagEvent("conversion", { send_to: "AW-776917579/Vml4CN-Hw94CEMuku_IC" });
}

/**
 * log a page view
 *
 * TODO: ideally don't export this function, so that most pages don't have to worry about logging
 * page views (should be automatic ideally)
 *
 * @param pageName string | undefined TODO: remove, and use getPageSource()
 * @param params {} extra data
 */
export function pageView(pageName: string | undefined, params: Record<string, unknown>): void {
  const amplitudeInstance = getAmplitudeInstance();
  pageName = pageName || getPageSource();

  if (process.env.NODE_ENV === "development") {
    console.log(`Page View: ${pageName}`, params);
  }

  if (amplitudeInstance) {
    amplitudeInstance.track(`Page View: ${pageName}`, params);
  }
}

export const usePageView = (pageName: string, params: Record<string, unknown>): void => {
  useEffect(() => {
    pageView(pageName, params);
  }, [pageName, params]);
};

export const TrackingContext = React.createContext({ data: {} });

type TrackingProviderProps = PropsWithChildren<{
  data: EventData;
}>;

export function TrackingProvider({ data, children }: TrackingProviderProps): JSX.Element {
  const { data: inheritedData } = useContext(TrackingContext);
  return (
    <TrackingContext.Provider value={{ data: { ...inheritedData, ...data } }}>
      {children}
    </TrackingContext.Provider>
  );
}

export function TrackingProviderWithFeatureFlags({
  trackingData,
  children,
}: {
  trackingData?: EventData;
  children: React.ReactNode;
}) {
  const devcycleClient = useDevCycleClient();
  const features = devcycleClient.allFeatures();
  const amplitudeInstance = getAmplitudeInstance();
  if (amplitudeInstance) {
    const identify = new amplitudeInstance.Identify();
    identify.set("enabled_feature_flags", Object.keys(features));
    amplitudeInstance.identify(identify);
  }

  return <TrackingProvider data={{ ...(trackingData ?? {}) }}>{children}</TrackingProvider>;
}

export type TrackingEvent = {
  name: EventNames;
  data?: EventData;
};

export type Tracking = {
  logEvent: (eventName: EventNames, data?: EventData) => void;
  logEvents: (...events: TrackingEvent[]) => void;
  trackingData: EventData;
};

export function useTracking(): Tracking {
  const { data: inheritedData } = useContext(TrackingContext);
  return useMemo(() => {
    return {
      logEvent: (eventName: EventNames, data?: EventData) =>
        logEvent(eventName, { ...inheritedData, ...data }),
      logEvents: (...events: TrackingEvent[]) => {
        for (const { name, data } of events) {
          logEvent(name, { ...inheritedData, ...data });
        }
      },
      trackingData: inheritedData,
    };
  }, [inheritedData]);
}
